import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  InvoiceQueryParams,
  useInvoiceQuery,
} from '@/hooks/billing/useInvoiceQuery'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { BillingRegister } from '@/interfaces/billing'
import { storage } from '@/lib/utils'

import {
  BillingFilters,
  safeParseIntId,
  useBillingFilters,
} from '../billingFilters/hooks/useBillingFilters'
import { useBusinessUserQuery } from '@/hooks/business/useBusinessUserQuery'

// Define a more complete interface for invoice data
interface InvoiceData {
  items: BillingRegister[]
  totalItems: number
  currentPage: number
  totalPages: number
}

// Define the context type
interface TableContextType {
  currentPage: number
  setCurrentPage: (_page: number) => void
  isFetching: boolean
  isError: boolean
  pageSize: number
  setPageSize: (_size: number) => void
  invoiceData: InvoiceData | null
  isLoading: boolean
  refetch: () => void
  // Add direct access to pagination methods for convenience
  goToNextPage: () => void
  goToPreviousPage: () => void
  // Skeleton data for loading state
  skeletonRowCount: number
  // Additional helpful properties
  hasNextPage: boolean
  hasPreviousPage: boolean
  // Filter management
  resetToFirstPage: () => void
}

// Create the context with a default value
const TableContext = createContext<TableContextType | undefined>(undefined)

// Default values
export const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 20

// Storage keys
export const BILLING_STORAGE_KEYS = {
  CURRENT_PAGE: 'billing-table-current-page',
  PAGE_SIZE: 'billing-table-page-size',
} as const

// Helper to create a filter key for comparison
const createFilterKey = (filters: BillingFilters): string => {
  return JSON.stringify({
    search: filters.search || '',
    status: [...filters.status].sort(),
    paymentStatus: [...filters.paymentStatus].sort(),
    origin: [...filters.origin].sort(),
    dateFrom: filters.dateFrom || '',
    dateTo: filters.dateTo || '',
    dateType: filters.dateType || 'emision',
    amountFrom: filters.amountFrom || '',
    amountTo: filters.amountTo || '',
    codeCurrency: filters.codeCurrency || '',
    documentType: filters.documentType || '',
  })
}

// Provider component that will wrap the table components
export const TableContextProvider = ({ children }: { children: ReactNode }) => {
  // Get filters from the billing filters hook (now with built-in localStorage persistence)
  const { filters } = useBillingFilters()

  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})

  const { user, allowRoles } = useBusinessUserQuery({
    businessId: businessData?.id ?? 0,
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })

  // Track previous filter state to detect changes
  const previousFiltersRef = useRef<string>('')
  const isInitialMount = useRef(true)

  // Initialize state from localStorage
  const [currentPage, setCurrentPageState] = useState(() =>
    storage.get(BILLING_STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
  )

  const [pageSize, setPageSizeState] = useState(() =>
    storage.get(BILLING_STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE)
  )

  // Check if filters have changed (excluding pagination-related changes)
  const currentFilterKey = createFilterKey(filters)
  const filtersHaveChanged =
    previousFiltersRef.current !== currentFilterKey && !isInitialMount.current

  // Update previous filters reference
  useEffect(() => {
    previousFiltersRef.current = currentFilterKey
    isInitialMount.current = false
  }, [currentFilterKey])

  // Reset to page 1 when filters change (but not on initial mount)
  useEffect(() => {
    if (filtersHaveChanged && currentPage !== INITIAL_PAGE) {
      setCurrentPageState(INITIAL_PAGE)
      storage.set(BILLING_STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
    }
  }, [filtersHaveChanged, currentPage])

  // Build query parameters from current state and filters
  const queryParams: InvoiceQueryParams = useMemo(() => {
    return {
      page: currentPage,
      per_page: pageSize,
      search: filters.search || null,

      // Basic status filters
      ...(filters.status.includes('all')
        ? {}
        : {
            status: filters.status.join(','),
          }),
      ...(filters.documentType === 'all'
        ? {}
        : {
            isPreReceipt: filters.documentType === 'prefactura',
          }),
      ...(filters.paymentStatus.includes('all')
        ? {}
        : {
            paymentStatus: filters.paymentStatus.join(','),
          }),
      ...(filters.origin.includes('all')
        ? {}
        : {
            origin: filters.origin.join(','),
          }),

      // Date filters based on dateType
      ...(filters.dateFrom || filters.dateTo
        ? (() => {
            // Map dateType to the appropriate date fields
            switch (filters.dateType) {
              case 'emision':
                return {
                  dateFrom: filters.dateFrom || null,
                  dateTo: filters.dateTo || null,
                }
              case 'vencimiento':
                return {
                  paymentDeadlineFrom: filters.dateFrom || null,
                  paymentDeadlineTo: filters.dateTo || null,
                }
              case 'entrega':
                return {
                  deliveryFrom: filters.dateFrom || null,
                  deliveryTo: filters.dateTo || null,
                }
              case 'facturacion':
                return {
                  billFrom: filters.dateFrom || null,
                  billTo: filters.dateTo || null,
                }
              default:
                return {
                  dateFrom: filters.dateFrom || null,
                  dateTo: filters.dateTo || null,
                }
            }
          })()
        : {}),

      // Amount filters
      ...(filters.amountFrom || filters.amountTo
        ? {
            amountFrom: filters.amountFrom || null,
            amountTo: filters.amountTo || null,
          }
        : {}),
      ...(filters.amountFrom || filters.amountTo
        ? filters.codeCurrency
          ? { amountCodeCurrency: filters.codeCurrency }
          : { amountCodeCurrency: 'CUP' }
        : {}),

      // Advanced filters
      ...(filters.clientId ? { clientId: filters.clientId } : {}),
      ...(filters.commercialId ? { commercialId: filters.commercialId } : {}),
      ...(filters.paymentGatewayIds.length > 0
        ? { paymentGatewayIds: filters.paymentGatewayIds.join(',') }
        : {}),
      ...(filters.productsId.length > 0
        ? { productsId: filters.productsId.join(',') }
        : {}),
      ...(filters.areaSalesIds.length > 0
        ? { areaSalesIds: filters.areaSalesIds.join(',') }
        : {}),

      // Boolean filters
      ...(filters.hasDiscounts ? { hasDiscounts: filters.hasDiscounts } : {}),
      ...(filters.hasCommissions
        ? { hasCommissions: filters.hasCommissions }
        : {}),
      ...(filters.houseCosted ? { houseCosted: filters.houseCosted } : {}),
      ...(filters.isForTakeAway
        ? { isForTakeAway: filters.isForTakeAway }
        : {}),
      ...(filters.pickUpInStore
        ? { pickUpInStore: filters.pickUpInStore }
        : {}),
      ...(filters.modifiedPrice
        ? { modifiedPrice: filters.modifiedPrice }
        : {}),
      ...(filters.hasTips ? { hasTips: filters.hasTips } : {}),
      ...(filters.isPreDispatched
        ? { isPreDispatched: filters.isPreDispatched }
        : {}),
      ...(filters.isDispatched ? { isDispatched: filters.isDispatched } : {}),

      // User-related filters
      ...(filters.managedByIds.length > 0
        ? { managedByIds: filters.managedByIds.join(',') }
        : {}),
      ...(filters.salesByIds.length > 0
        ? { salesByIds: filters.salesByIds.join(',') }
        : {}),
      ...(filters.shippingByIds.length > 0
        ? { shippingByIds: filters.shippingByIds.join(',') }
        : {}),

      // System-related filters
      ...(filters.economicCycleIds.length > 0
        ? { economicCycleIds: filters.economicCycleIds.join(',') }
        : {}),
      ...(filters.priceSystemIds.length > 0
        ? { priceSystemIds: filters.priceSystemIds.join(',') }
        : {}),

      // Coupon filters
      ...(filters.coupons.length > 0
        ? { coupons: filters.coupons.join(',') }
        : {}),

      // Location filters - convert strings to numbers for API
      ...(filters.provinceId
        ? { provinceId: safeParseIntId(filters.provinceId) }
        : {}),
      ...(filters.municipalityId
        ? { municipalityId: safeParseIntId(filters.municipalityId) }
        : {}),
    }
  }, [currentPage, pageSize, filters])

  const filteredQueryParams = useMemo(() => {
    const allowedAreas = user?.allowedSalesAreas ?? []
    const isManagerSales = allowRoles(['MANAGER_SALES'], true)
    const isOwner = allowRoles(['OWNER'], true)

    if (filters.areaSalesIds.length > 0) {
      return queryParams
    }

    if (allowedAreas.length > 0 && isManagerSales && !isOwner) {
      return {
        ...queryParams,
        areaSalesIds: allowedAreas.map((a) => a.id).join(','),
      }
    }

    return queryParams
  }, [
    allowRoles,
    filters.areaSalesIds.length,
    queryParams,
    user?.allowedSalesAreas,
  ])

  // Use the invoice query hook with the current parameters
  const {
    data: invoiceData,
    isLoading,
    isFetching,
    isError,
    isPending,
    isRefetching,
    refetch,
    pagination,
  } = useInvoiceQuery({
    params: filteredQueryParams,
    options: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: true,
    },
    businessId: businessData?.id ?? 0,
  })

  // Handle page changes with localStorage persistence
  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page)
    storage.set(BILLING_STORAGE_KEYS.CURRENT_PAGE, page)
  }, [])

  // Handle page size changes with localStorage persistence
  const setPageSize = useCallback(
    (size: number) => {
      setPageSizeState(size)
      storage.set(BILLING_STORAGE_KEYS.PAGE_SIZE, size)

      // Reset to first page when changing page size (if not already on page 1)
      if (currentPage !== INITIAL_PAGE) {
        setCurrentPage(INITIAL_PAGE)
      }
    },
    [currentPage, setCurrentPage]
  )

  // Navigation helpers
  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      setCurrentPage(currentPage + 1)
    }
  }, [currentPage, setCurrentPage, pagination.hasNextPage])

  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      setCurrentPage(currentPage - 1)
    }
  }, [currentPage, setCurrentPage, pagination.hasPreviousPage])

  // Helper to reset to first page (useful for external components)
  const resetToFirstPage = useCallback(() => {
    if (currentPage !== INITIAL_PAGE) {
      setCurrentPage(INITIAL_PAGE)
    }
  }, [currentPage, setCurrentPage])

  // Sync with API response if there's a mismatch (fallback safety)
  useEffect(() => {
    if (invoiceData?.currentPage && invoiceData.currentPage !== currentPage) {
      setCurrentPageState(invoiceData.currentPage)
      storage.set(BILLING_STORAGE_KEYS.CURRENT_PAGE, invoiceData.currentPage)
    }
  }, [invoiceData?.currentPage, currentPage])

  // Create the context value object
  const contextValue: TableContextType = {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    invoiceData: invoiceData || null,
    isLoading: isLoading || isPending || isRefetching,
    isFetching,
    isError,
    refetch,
    goToNextPage,
    goToPreviousPage,
    skeletonRowCount: pageSize,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    resetToFirstPage,
  }

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  )
}

// Custom hook to use the table context
export const useTableContext = () => {
  const context = useContext(TableContext)
  if (context === undefined) {
    throw new Error(
      'useTableContext must be used within a TableContextProvider'
    )
  }
  return context
}

// Export storage utilities for external use if needed
export const tableStorage = {
  clearAll: () => {
    Object.values(BILLING_STORAGE_KEYS).forEach((key) => storage.remove(key))
  },
  getSavedPage: () =>
    storage.get(BILLING_STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE),
  getSavedPageSize: () =>
    storage.get(BILLING_STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE),
}
